import React, { useState, useEffect } from 'react';
import axios from 'axios';

const ServicesModal = ({ isOpen, onClose, initialData }) => {
  const [servicesData, setServicesData] = useState({
    title: "",
    description: "",
    services: [],
    buttonText: "",
    buttonLink: ""
  });

  const [activeTab, setActiveTab] = useState('main');
  const [isLoading, setIsLoading] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [currentUploadIndex, setCurrentUploadIndex] = useState(null);

  useEffect(() => {
    const fetchCurrentData = async () => {
      if (isOpen && !initialData) {
        try {
          setIsLoading(true);
          const response = await fetch(`${import.meta.env.VITE_API_URL}/home/services/`);
          if (response.ok) {
            const data = await response.json();
            setServicesData({
              title: data.title || "",
              description: data.description || "",
              services: data.services || [],
              buttonText: data.buttonText || "",
              buttonLink: data.buttonLink || ""
            });
          }
        } catch (error) {
          console.error('Error fetching services data:', error);
        } finally {
          setIsLoading(false);
        }
      }
    };

    fetchCurrentData();
  }, [isOpen, initialData]);

  useEffect(() => {
    if (initialData) {
      setServicesData(initialData);
    }
  }, [initialData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setServicesData(prev => ({ ...prev, [name]: value }));
  };

  const handleServiceChange = (index, field, value) => {
    const updatedServices = [...servicesData.services];
    updatedServices[index] = { ...updatedServices[index], [field]: value };
    setServicesData(prev => ({ ...prev, services: updatedServices }));
  };

  const addService = () => {
    setServicesData(prev => ({
      ...prev,
      services: [...prev.services, { 
        title: "", 
        icon: "", 
        color: "#0760ad", 
        link: "" 
      }]
    }));
  };

  const removeService = (index) => {
    const updatedServices = [...servicesData.services];
    updatedServices.splice(index, 1);
    setServicesData(prev => ({ ...prev, services: updatedServices }));
  };

  const handleIconUpload = async (e, index) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('image', file);
    formData.append('category', 'service_icons');

    try {
      setIsUploading(true);
      setCurrentUploadIndex(index);
      setUploadProgress(0);

      const response = await axios.post(`${import.meta.env.VITE_API_URL}/images/`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          setUploadProgress(percentCompleted);
        }
      });

      const imageUrl = response.data.image || response.data.image.url;
      handleServiceChange(index, 'icon', imageUrl);
    } catch (error) {
      console.error('Error uploading icon:', error);
      alert('Failed to upload icon. Please try again.');
    } finally {
      setIsUploading(false);
      setCurrentUploadIndex(null);
      setUploadProgress(0);
    }
  };

  const handleSubmit = async () => {
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/home/services/`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(servicesData)
      });

      if (response.ok) {
        const updatedData = await response.json();

        onClose();
      } else {
        console.error('Failed to update services data');
        alert('Failed to save changes. Please try again.');
      }
    } catch (error) {
      console.error('Error updating services data:', error);
      alert('An error occurred while saving. Please try again.');
    }
  };

  if (isLoading) {
    return (
      <dialog className={`modal ${isOpen ? 'modal-open' : ''}`}>
        <div className="modal-box">
          <span className="loading loading-spinner loading-lg"></span>
          <p>Loading services data...</p>
        </div>
      </dialog>
    );
  }

  return (
    <dialog className={`modal ${isOpen ? 'modal-open' : ''}`}>
      <div className="modal-box w-11/12 max-w-5xl max-h-[90vh] relative">
        <button onClick={onClose} className="btn btn-sm btn-circle absolute right-2 top-2">✕</button>
        <h3 className="font-bold text-lg mb-4">Edit Services Section</h3>
        
        <div className="tabs tabs-boxed mb-6">
          <button 
            className={`tab ${activeTab === 'main' ? 'tab-active' : ''}`}
            onClick={() => setActiveTab('main')}
          >
            Main Content
          </button>
          <button 
            className={`tab ${activeTab === 'services' ? 'tab-active' : ''}`}
            onClick={() => setActiveTab('services')}
          >
            Services ({servicesData.services.length})
          </button>
        </div>
        
        <div className="overflow-y-auto max-h-[60vh] pr-2">
          {activeTab === 'main' && (
            <div className="space-y-4">
              <div className="form-control">
                <label className="label">
                  <span className="label-text">Section Title</span>
                </label>
                <input 
                  type="text" 
                  name="title" 
                  value={servicesData.title || ''} 
                  onChange={handleChange} 
                  className="input input-bordered w-full" 
                />
              </div>
              
              <div className="form-control">
                <label className="label">
                  <span className="label-text">Description</span>
                </label>
                <textarea 
                  name="description" 
                  value={servicesData.description || ''} 
                  onChange={handleChange} 
                  className="textarea textarea-bordered w-full h-32" 
                />
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="form-control">
                  <label className="label">
                    <span className="label-text">Button Text</span>
                  </label>
                  <input 
                    type="text" 
                    name="buttonText" 
                    value={servicesData.buttonText || ''} 
                    onChange={handleChange} 
                    className="input input-bordered w-full" 
                  />
                </div>
                
                <div className="form-control">
                  <label className="label">
                    <span className="label-text">Button Link</span>
                  </label>
                  <input 
                    type="text" 
                    name="buttonLink" 
                    value={servicesData.buttonLink || ''} 
                    onChange={handleChange} 
                    className="input input-bordered w-full" 
                  />
                </div>
              </div>
            </div>
          )}
          
          {activeTab === 'services' && (
            <div className="space-y-4">
              <div className="flex justify-between items-center mb-4">
                <h4 className="font-bold">Services List</h4>
                <button 
                  onClick={addService}
                  className="btn btn-sm btn-primary"
                >
                  Add Service
                </button>
              </div>

              {servicesData.services.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  No services added yet. Click "Add Service" to create one.
                </div>
              ) : (
                servicesData.services.map((service, index) => (
                  <div key={index} className="card bg-base-100 shadow-sm p-4 relative">
                    <button 
                      onClick={() => removeService(index)}
                      className="btn btn-sm btn-circle btn-error absolute -top-2 -right-2 z-10"
                    >
                      ✕
                    </button>
                    
                    <h4 className="font-bold mb-3">Service {index + 1}</h4>
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                      <div className="form-control">
                        <label className="label">
                          <span className="label-text">Title</span>
                        </label>
                        <input 
                          type="text" 
                          value={service?.title || ''} 
                          onChange={(e) => handleServiceChange(index, 'title', e.target.value)} 
                          className="input input-bordered w-full" 
                        />
                      </div>
                      
                      <div className="form-control">
                        <label className="label">
                          <span className="label-text">Icon</span>
                        </label>
                        <div className="flex items-center gap-2">
                          {service?.icon && (
                            <div className="avatar">
                              <div className="w-8 rounded">
                                <img 
                                  src={service.icon} 
                                  alt="Service icon" 
                                  onError={(e) => {
                                    e.target.onerror = null;
                                    e.target.src = 'https://via.placeholder.com/40?text=Icon';
                                  }}
                                />
                              </div>
                            </div>
                          )}
                          <input 
                            type="file" 
                            accept="image/*"
                            onChange={(e) => handleIconUpload(e, index)}
                            className="file-input file-input-bordered file-input-sm w-full max-w-xs"
                            disabled={isUploading && currentUploadIndex === index}
                          />
                          {isUploading && currentUploadIndex === index && (
                            <progress 
                              className="progress progress-primary w-20" 
                              value={uploadProgress} 
                              max="100"
                            ></progress>
                          )}
                        </div>
                      </div>
                      
                      <div className="form-control">
                        <label className="label">
                          <span className="label-text">Color (hex)</span>
                        </label>
                        <div className="flex items-center gap-2">
                          <input 
                            type="color" 
                            value={service?.color || '#0760ad'} 
                            onChange={(e) => handleServiceChange(index, 'color', e.target.value)} 
                            className="h-10 w-10 p-1 rounded border"
                          />
                          <input 
                            type="text" 
                            value={service?.color || ''} 
                            onChange={(e) => handleServiceChange(index, 'color', e.target.value)} 
                            className="input input-bordered w-full" 
                            placeholder="#0760ad"
                          />
                        </div>
                      </div>
                      
                      <div className="form-control">
                        <label className="label">
                          <span className="label-text">Link</span>
                        </label>
                        <input 
                          type="text" 
                          value={service?.link || ''} 
                          onChange={(e) => handleServiceChange(index, 'link', e.target.value)} 
                          className="input input-bordered w-full" 
                          placeholder="/service-path"
                        />
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </div>
        
        <div className="modal-action mt-4">
          <button className="btn" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSubmit}>
            Save Changes
          </button>
        </div>
      </div>
    </dialog>
  );
};

export default ServicesModal;